import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { actionCreators, selectors, sliceKeys } from '../../model';
import { useFilteredData, filterTypes } from '../../shared/packages/filter';
import { relatedRoomTreeBySearch } from '../../shared/configs/filterOptions';
import ColListWrapper from '../../shared/ui/ColListWrapper';
import Subtitle from '../../shared/ui/Subtitle';
import BaseRoomItem from '../../shared/ui/BaseRoomItem';
import { createHouseAddress } from '../../shared/packages/address';
import UnlinkButton from './UnlinkButton';
import NotFound from '../../shared/ui/NotFound';
import styles from './index.module.scss';

const { selectSearch } = selectors[sliceKeys.relatedRooms];
const { selectRelatedRooms } = selectors[sliceKeys.houses];

const { relationDeleted, roomFromRelationDeleted } =
  actionCreators[sliceKeys.houses];

const MemoizedItem = memo(FirstLevelItem);

const RelatedRoomsList = ({ children }) => {
  const relatedRooms = useSelector(selectRelatedRooms);

  const roomsTree = useMemo(() => createTree(relatedRooms), [relatedRooms]);

  const search = useSelector(selectSearch);
  const filteredData = useFilteredData({
    data: roomsTree,
    value: search,
    filterType: filterTypes.treeBySearch,
    options: relatedRoomTreeBySearch,
  });

  return (
    <div className="colContentWrap">
      {children}
      {filteredData.length ? (
        <ColListWrapper grouped scrollable>
          {filteredData.map((item) => (
            <MemoizedItem key={item.name} item={item} />
          ))}
        </ColListWrapper>
      ) : (
        <NotFound />
      )}
    </div>
  );
};

export default RelatedRoomsList;

function FirstLevelItem({ item }) {
  const { name, rooms } = item;

  return (
    <li>
      <Subtitle>{name}</Subtitle>
      <ColListWrapper>
        {rooms.map((room) => (
          <BaseRoomItem
            key={room.id}
            room={room}
            action={
              <UnlinkButton
                tooltipTitle="Удалить связку"
                action={relationDeleted(room)}
              />
            }
          >
            <ColListWrapper nested>
              {room.relatedRooms.map((relatedRoom) => {
                return (
                  <BaseRoomItem
                    key={relatedRoom.id}
                    room={relatedRoom}
                    info={
                      room.houseId !== relatedRoom.houseId && (
                        <span>{createHouseAddress(relatedRoom)}</span>
                      )
                    }
                  >
                    <UnlinkButton
                      tooltipTitle="Удалить помещение из связки"
                      action={roomFromRelationDeleted(relatedRoom)}
                      className={styles.removeRoomBtn}
                    />
                  </BaseRoomItem>
                );
              })}
            </ColListWrapper>
          </BaseRoomItem>
        ))}
      </ColListWrapper>
    </li>
  );
}

function createTree(rooms) {
  const output = rooms.reduce((acc, room) => {
    if (!acc.has(room.houseId)) {
      acc.set(room.houseId, { name: createHouseAddress(room), rooms: [] });
    }

    acc.get(room.houseId).rooms.push(room);

    return acc;
  }, new Map());

  return [...output.values()].sort((a, b) =>
    a.name.localeCompare(b.name, 'ru', { numeric: true })
  );
}
